'use client';

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from 'react';
import algoliasearch from 'algoliasearch/lite';
import Cookies from 'js-cookie';

import { CartContext } from './CartContext';

interface Dealer {
  id: number;
  name: string;
  slug: string;
  email: string;
  phone: string;
  address: {
    line1: string;
    line2: string;
    line3: string;
    city: string;
    state: string;
    zip: string;
    country: string;
    latitude: number;
    longitude: number;
  };
  isCOS: boolean;
  mainServiceArea: string;
  areasOfService: {
    children: {
      text: string;
    }[];
  }[];
  objectID: string;
  _highlightResult: {
    id: {
      value: string;
      matchLevel: string;
      fullyHighlighted: boolean;
      matchedWords: string[];
    };
    name: {
      value: string;
      matchLevel: string;
      matchedWords: string[];
    };
    address: {
      city: {
        value: string;
        matchLevel: string;
        matchedWords: string[];
      };
      state: {
        value: string;
        matchLevel: string;
        matchedWords: string[];
      };
    };
    mainServiceArea: {
      value: string;
      matchLevel: string;
      matchedWords: string[];
    };
  };
  internal: {
    contentDigest: string;
    type: string;
    owner: string;
  };
  [key: string]: any;
}

export interface DealerSearchContextType {
  dealerSearchActive: boolean;
  dealerSearchResults: Dealer[];
  setDealerSearchActive: (active: boolean) => void;
  toggleDealerSearch: () => void;
  getDealerSearchResults: (zipcode: string) => Promise<void>;
  deleteDealerSearchResults: () => void;
  firstLoad: boolean;
  setFirstLoad: (val: boolean) => void;
  loading: boolean;
  storedDealer: Dealer | null;
  /**
   * Loads the dealer saved in the `dealerId` cookie. Pass `{ selected: true }`
   * when the shopper has just picked the dealer, which counts it as a
   * selection (see `dealerSelectionCount`) once the dealer has loaded.
   */
  fetchStoredDealerData: (options?: { selected?: boolean }) => Promise<void>;
  setDealerSearchResults: (results: Dealer[]) => void;
  setDealerGateModalActive: (active: boolean) => void;
  dealerGateModalActive: boolean;
  dealerSearchDisplayMode: string;
  setDealerSearchDisplayMode: (mode: string) => void;
  setStoredDealer: (dealer: Dealer | null) => void;
  storedDealerPanelVisible: boolean;
  setStoredDealerPanelVisible: (visible: boolean) => void;
  /** The postal code most recently searched in the dealer search. */
  searchedPostalCode: string | null;
  /**
   * The postal code that was searched when the shopper selected
   * `storedDealer` — kept paired with the dealer so other parts of the site
   * (e.g. the Find My Fit pricing card) can show the same ZIP the shopper
   * entered. Restored from local storage alongside the `dealerId` cookie,
   * and only when that storage was saved for this same dealer (see
   * `rememberStoredDealerPostalCode`) — otherwise null.
   */
  storedDealerPostalCode: string | null;
  /**
   * Saves `searchedPostalCode` as the postal code paired with the dealer
   * being selected, recording the dealer's id with it. The id matters: the
   * `dealerId` cookie is shared across subdomains but local storage is per
   * site, so a stored ZIP is only valid for the dealer it was saved with.
   */
  rememberStoredDealerPostalCode: (dealerId: number | string) => void;
  /**
   * How many dealer selections have completed since this page loaded. It goes
   * up in the same update as the new `storedDealer` — only when the shopper
   * picked a dealer and it loaded (not when the `dealerId` cookie is restored
   * on load, and not when the lookup fails) — so other parts of the site can
   * tell a choice from a restore without tracking anything half-finished.
   */
  dealerSelectionCount: number;
}

interface DealerSearchProviderProps {
  children: ReactNode;
}

const client = algoliasearch(
  process.env.GATSBY_ALGOLIA_APP_ID,
  process.env.GATSBY_ALGOLIA_SEARCH_KEY
);

// select algolia index
const dealerInformationIndex = client.initIndex('dealers');

const STORED_DEALER_POSTAL_CODE_KEY = 'dealerPostalCode';

/**
 * Cleans up a searched postal code before it is saved and shared as "the ZIP
 * the shopper searched": trims whitespace and reduces a US ZIP+4 (`80202-1234`
 * or `802021234`) to its 5-digit ZIP. The header search validates the trimmed,
 * ZIP+4-tolerant value but hands the raw text on, so without this the stored
 * ZIP could be `" 80202 "` or `"80202-1234"`. Other codes (e.g. an already
 * formatted Canadian `K1A 0B1`) are only trimmed. This is for the saved pair
 * only; the dealer search request itself is unchanged.
 */
function normalizeSearchedPostalCode(raw: string): string {
  const trimmed = raw.trim();
  const zipPlus4 = /^(\d{5})-?\d{4}$/.exec(trimmed);
  return zipPlus4 ? zipPlus4[1] : trimmed;
}

/**
 * The searched ZIP is stored together with the id of the dealer it was
 * searched for: `{ "dealerId": "42", "postalCode": "80202" }`. A bare ZIP
 * (the earlier format) has no dealer to check against, so it is ignored.
 */
function readStoredDealerPostalCode(dealerId: number | string): string | null {
  try {
    const raw = window.localStorage.getItem(STORED_DEALER_POSTAL_CODE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as {
      dealerId?: unknown;
      postalCode?: unknown;
    } | null;
    // Only a string or number counts as an id: stringifying anything else
    // (an object, an array like [42]) would give a value that can wrongly
    // match, or "[object Object]".
    const storedDealerId =
      typeof parsed?.dealerId === 'string' ||
      typeof parsed?.dealerId === 'number'
        ? String(parsed.dealerId)
        : null;
    if (
      parsed &&
      typeof parsed === 'object' &&
      storedDealerId !== null &&
      storedDealerId === String(dealerId) &&
      typeof parsed.postalCode === 'string' &&
      parsed.postalCode.trim()
    ) {
      // Normalized on the way out too, for pairs saved before this cleanup.
      return normalizeSearchedPostalCode(parsed.postalCode);
    }
    return null;
  } catch {
    // Storage blocked (private browsing etc.) or not our JSON — the dealer
    // still works, it just isn't paired with the ZIP that was searched.
    return null;
  }
}

function writeStoredDealerPostalCode(
  dealerId: number | string,
  postalCode: string | null
): void {
  try {
    if (postalCode) {
      window.localStorage.setItem(
        STORED_DEALER_POSTAL_CODE_KEY,
        JSON.stringify({
          dealerId: String(dealerId),
          postalCode: normalizeSearchedPostalCode(postalCode),
        })
      );
    } else {
      window.localStorage.removeItem(STORED_DEALER_POSTAL_CODE_KEY);
    }
  } catch {
    // See readStoredDealerPostalCode.
  }
}
const DealerSearchContext = createContext<DealerSearchContextType | undefined>(
  undefined
);

function DealerSearchProvider({
  children,
}: Readonly<DealerSearchProviderProps>) {
  const cartContext = useContext(CartContext);
  const [dealerSearchActive, setDealerSearchActive] = useState(false);
  const [dealerGateModalActive, setDealerGateModalActive] = useState(false);
  const [dealerSearchResults, setDealerSearchResults] = useState([]);
  const [dealerSearchDisplayMode, setDealerSearchDisplayMode] = useState('');
  const [loading, setLoading] = useState(false);
  const [firstLoad, setFirstLoad] = useState(true);
  const [storedDealer, setStoredDealer] = useState<Dealer | null>(null);
  const [storedDealerPanelVisible, setStoredDealerPanelVisible] =
    useState(false);
  const [searchedPostalCode, setSearchedPostalCode] = useState<string | null>(
    null
  );
  const [storedDealerPostalCode, setStoredDealerPostalCode] = useState<
    string | null
  >(null);

  const [dealerSelectionCount, setDealerSelectionCount] = useState(0);

  const rememberStoredDealerPostalCode = (dealerId: number | string) => {
    writeStoredDealerPostalCode(dealerId, searchedPostalCode);
  };

  const fetchStoredDealerData = async (options?: { selected?: boolean }) => {
    const dealerId = Cookies.get('dealerId');
    try {
      // fetch dealer data from Algolia
      const response = await dealerInformationIndex.search<Dealer>(
        dealerId || '',
        {
          restrictSearchableAttributes: ['id'],
        }
      );

      if (response.hits.length) {
        const dealer = response.hits[0] as Dealer;
        setStoredDealer(dealer);
        // Only the ZIP saved for this very dealer: the cookie that picked
        // the dealer is shared across subdomains, this storage is not.
        setStoredDealerPostalCode(readStoredDealerPostalCode(dealer.id));
        // Counted here, with the dealer, so a selection that fails to load
        // (or finds no dealer) is never counted and nothing is left pending.
        if (options?.selected) setDealerSelectionCount(count => count + 1);
        setDealerGateModalActive(false);
        setDealerSearchDisplayMode('');
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    // does cookie have store dealerId? Populate storedDealer
    // with fresh dealer data from Algolia
    if (typeof window !== 'undefined' && Cookies.get('dealerId')) {
      (async () => {
        await fetchStoredDealerData();
      })();
    }
  }, []);

  useEffect(() => {
    // if firstLoad is true, delete dealerSearchResults
    if (firstLoad) {
      setDealerSearchResults([]);
    }
  }, [firstLoad]);

  useEffect(() => {
    const updateDealerInCart = async () => {
      if (cartContext?.cart?.id && storedDealer?.id) {
        const attributes = [
          {
            key: 'Dealer ID',
            value: storedDealer?.id?.toString() || '',
          },
        ];

        await cartContext?.updateCartAttributes(attributes);
      }
    };
    updateDealerInCart();
  }, [storedDealer, cartContext?.cart?.id]);

  const getDealerSearchResults = async (zipcode: string) => {
    setFirstLoad(false);
    setSearchedPostalCode(normalizeSearchedPostalCode(zipcode));
    setLoading(true); // spinner hook
    try {
      const response = await fetch(
        `${process.env.GATSBY_POSTCODE_SEARCH_API}?postalCode=${zipcode}`,
        {
          method: 'GET',
        }
      );

      setDealerSearchResults(await response.json());

      setLoading(false);
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  const toggleDealerSearch = () => {
    setDealerSearchActive(!dealerSearchActive);
  };

  const deleteDealerSearchResults = () => {
    setDealerSearchResults([]);
  };

  const value = useMemo(
    () => ({
      dealerSearchActive,
      dealerSearchResults,
      setDealerSearchActive,
      toggleDealerSearch,
      getDealerSearchResults,
      deleteDealerSearchResults,
      firstLoad,
      setFirstLoad,
      loading,
      storedDealer,
      fetchStoredDealerData,
      setDealerSearchResults,
      setDealerGateModalActive,
      dealerGateModalActive,
      dealerSearchDisplayMode,
      setDealerSearchDisplayMode,
      setStoredDealer,
      storedDealerPanelVisible,
      setStoredDealerPanelVisible,
      searchedPostalCode,
      storedDealerPostalCode,
      rememberStoredDealerPostalCode,
      dealerSelectionCount,
    }),
    [
      dealerSearchResults,
      dealerSearchActive,
      storedDealer,
      setFirstLoad,
      fetchStoredDealerData,
      dealerSearchDisplayMode,
      storedDealerPanelVisible,
      searchedPostalCode,
      storedDealerPostalCode,
      dealerSelectionCount,
    ]
  );

  return (
    <DealerSearchContext.Provider value={value}>
      {children}
    </DealerSearchContext.Provider>
  );
}

export { DealerSearchProvider, DealerSearchContext };
